import type { FormField } from '@funkit/connect-core'
import { type ReactNode, type RefObject, useState } from 'react'
import {
  Pressable,
  StyleSheet,
  TextInput,
  type TextInputProps,
  View,
} from 'react-native'
import { FigmaChevronDownIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { inputTextStyle } from '../theme/inputTextStyle'
import { Box, Text, useTheme } from '../theme/restyle'
import { formatCalendarDate } from './calendarDate'
import { type FieldAutofill, fieldAutofill } from './fieldAutofill'
import { fieldKeyboardType } from './formLayout'
import { PADS_WITHOUT_RETURN_KEY } from './keyboardReturnKey'
import type { KycFieldIssue } from './kycFormState'
import { KYC_FORM_METRICS } from './kycFormTheme'

/**
 * What the return key does at the end of a field.
 *
 * `next` hands focus on without dropping the keyboard; `done` ends the step,
 * because the last field has nowhere left to go.
 */
/** However the holder wants the box's node — a ref object or a callback. */
export type FieldInputRef =
  | RefObject<TextInput | null>
  | ((node: TextInput | null) => void)

export interface FieldReturnKey {
  kind: 'next' | 'done'
  onPress: () => void
}

export interface FiatFormFieldRowProps {
  field: FormField
  value: string
  /** Shown only once the page has been tried, so a blank form is quiet. */
  issue?: KycFieldIssue
  /** The step focuses through its fields, so it holds the first box's node. */
  inputRef?: FieldInputRef
  /** Absent on a field with nothing to type into. */
  returnKey?: FieldReturnKey
  /** Inside a section's card: the card fills and rounds it, so the box won't. */
  editable?: boolean
  autoFocus?: boolean
  grouped?: boolean
  /** A date a picker answers: the box shows the answer instead of taking it. */
  picked?: FieldPicked
  onChangeValue: (value: string) => void
  onBlur: () => void
}

/** A field answered by a picker rather than typed into. */
export interface FieldPicked {
  isOpen: boolean
  onOpen: () => void
}

/**
 * One field of a KYC form, drawn as the design draws its type.
 *
 * The label is the placeholder — the design has no label above the box — so a
 * field type this build has never seen still reads correctly from the server's
 * `labelFallback`.
 */
export function FiatFormFieldRow({
  field,
  value,
  issue,
  inputRef,
  returnKey,
  grouped,
  editable = true,
  autoFocus = false,
  picked,
  onChangeValue,
  onBlur,
}: FiatFormFieldRowProps) {
  return (
    <FieldControl
      field={field}
      value={value}
      invalid={issue !== undefined}
      inputRef={inputRef}
      returnKey={returnKey}
      editable={editable}
      autoFocus={autoFocus}
      grouped={grouped}
      picked={picked}
      onChangeValue={onChangeValue}
      onBlur={onBlur}
    />
  )
}

function FieldControl({
  field,
  value,
  invalid,
  inputRef,
  returnKey,
  grouped,
  editable = true,
  autoFocus = false,
  picked,
  onChangeValue,
  onBlur,
}: {
  field: FormField
  value: string
  invalid: boolean
  inputRef?: FieldInputRef
  returnKey?: FieldReturnKey
  editable?: boolean
  autoFocus?: boolean
  grouped?: boolean
  picked?: FieldPicked
  onChangeValue: (value: string) => void
  onBlur: () => void
}): ReactNode {
  const autofill = fieldAutofill(field)
  const { t, locale } = useFunkitTranslation()

  // A picked date is not typed into, so it is a value the picker writes
  // rather than a box that takes one.
  if (picked !== undefined) {
    return (
      <PickedBox
        label={field.labelFallback}
        value={value === '' ? undefined : formatCalendarDate(value, locale)}
        isOpen={picked.isOpen}
        editable={editable}
        grouped={grouped}
        invalid={invalid}
        onPress={picked.onOpen}
      />
    )
  }

  switch (field.type) {
    // The state chooser, drawn with the design's trailing chevron.
    case 'SELECT':
      return (
        <ChooserBox
          label={field.labelFallback}
          value={selectedLabel(field, value)}
          editable={editable}
          grouped={grouped}
          invalid={invalid}
          options={field.options}
          onSelect={onChangeValue}
        />
      )

    // Every other page is boxes. A type no frame draws never reaches here:
    // the form is refused whole, rather than part-drawn.
    default: {
      // The wire wants ISO 3166-1 alpha-2 in upper case. Core accepts either
      // case locally and nothing else upcases before send, so the box does.
      const isCountry = field.type === 'COUNTRY'
      return (
        <FieldBox
          value={value}
          placeholder={field.labelFallback}
          optional={!field.required}
          maxLength={isCountry ? 2 : undefined}
          keyboardType={fieldKeyboardType(field)}
          inputRef={inputRef}
          returnKey={returnKey}
          editable={editable}
          autoFocus={autoFocus}
          grouped={grouped}
          invalid={invalid}
          autofill={
            isCountry ? { ...autofill, autoCapitalize: 'characters' } : autofill
          }
          onChangeValue={
            isCountry
              ? (next) => onChangeValue(next.toUpperCase())
              : onChangeValue
          }
          onBlur={onBlur}
        />
      )
    }
  }
}

/**
 * The design's field: a filled box with the label as its placeholder, and an
 * "optional" caption under the label where the form does not require it.
 */
export function FieldBox({
  value,
  placeholder,
  optional = false,
  maxLength,
  align = 'left',
  keyboardType,
  inputRef,
  returnKey,
  grouped = false,
  editable = true,
  autoFocus = false,
  invalid = false,
  autofill,
  onChangeValue,
  onBlur,
}: {
  value: string
  placeholder: string
  optional?: boolean
  maxLength?: number
  align?: 'left' | 'center'
  keyboardType?: TextInputProps['keyboardType']
  inputRef?: FieldInputRef
  returnKey?: FieldReturnKey
  editable?: boolean
  autoFocus?: boolean
  grouped?: boolean
  /** A refused answer: the box takes the design's yellow. */
  invalid?: boolean
  autofill?: FieldAutofill
  onChangeValue: (value: string) => void
  onBlur: () => void
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const surface = fieldSurface(theme, grouped, invalid)
  const hasReturnKey =
    keyboardType === undefined || !PADS_WITHOUT_RETURN_KEY.has(keyboardType)

  return (
    <Box
      style={[
        styles.box,
        grouped ? styles.boxGrouped : undefined,
        { backgroundColor: surface.fill },
        optional ? styles.boxOptional : undefined,
      ]}
    >
      <TextInput
        ref={inputRef}
        value={value}
        editable={editable}
        autoFocus={autoFocus}
        accessibilityLabel={placeholder}
        onChangeText={onChangeValue}
        onBlur={onBlur}
        placeholder={placeholder}
        placeholderTextColor={surface.placeholder}
        maxLength={maxLength}
        keyboardType={keyboardType}
        // A pad has no return key, and asking for one makes iOS float its own
        // button over the screen. A filled box hands on there instead.
        returnKeyType={hasReturnKey ? returnKey?.kind : undefined}
        // Handing focus on must keep the keyboard: blurring first closes it,
        // and it reopens under the next field a beat later.
        submitBehavior="submit"
        onSubmitEditing={hasReturnKey ? returnKey?.onPress : undefined}
        // What lets the platform offer an address the user already has.
        textContentType={autofill?.textContentType}
        autoComplete={autofill?.autoComplete}
        autoCapitalize={autofill?.autoCapitalize}
        autoCorrect={false}
        spellCheck={false}
        style={[
          styles.input,
          inputTextStyle(theme),
          align === 'center' ? styles.inputCentered : undefined,
          { color: surface.content },
        ]}
      />
      {/* Pinned rather than stacked: the design keeps the placeholder on the
          same line as its neighbour's, and a caption in flow would lift it. */}
      {optional ? (
        <Text style={[styles.optional, { color: surface.placeholder }]}>
          {t('fiat.kycOptional')}
        </Text>
      ) : null}
    </Box>
  )
}

/**
 * A field whose answer a picker writes (1844:17471): the same box, showing
 * the answer or the label, and pressed to bring the picker back.
 */
function PickedBox({
  label,
  value,
  isOpen,
  grouped = false,
  editable = true,
  invalid = false,
  onPress,
}: {
  label: string
  value: string | undefined
  isOpen: boolean
  editable?: boolean
  autoFocus?: boolean
  grouped?: boolean
  invalid?: boolean
  onPress: () => void
}) {
  const theme = useTheme()
  const surface = fieldSurface(theme, grouped, invalid)

  return (
    <Pressable
      disabled={!editable}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityValue={value === undefined ? undefined : { text: value }}
      accessibilityState={{ expanded: isOpen }}
    >
      <Box
        style={[
          styles.box,
          grouped ? styles.boxGrouped : undefined,
          { backgroundColor: surface.fill },
        ]}
      >
        <Text
          variant="body"
          style={{
            color: value === undefined ? surface.placeholder : surface.content,
          }}
        >
          {value ?? label}
        </Text>
      </Box>
    </Pressable>
  )
}

/** A choice, drawn as a field with the design's trailing chevron. */
function ChooserBox({
  label,
  value,
  grouped = false,
  editable = true,
  invalid = false,
  options,
  onSelect,
}: {
  label: string
  value: string | undefined
  editable?: boolean
  autoFocus?: boolean
  grouped?: boolean
  invalid?: boolean
  options: readonly { id: string; labelFallback: string }[]
  onSelect: (id: string) => void
}) {
  const theme = useTheme()
  const surface = fieldSurface(theme, grouped, invalid)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Pressable
        disabled={!editable}
        onPress={() => setIsOpen((open) => !open)}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ expanded: isOpen }}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          style={[
            styles.box,
            styles.chooser,
            grouped ? styles.boxGrouped : undefined,
            { backgroundColor: surface.fill },
          ]}
        >
          {/* The row is space-between and the chevron does not shrink, so an
              unconstrained label would push it off the box's edge. */}
          <Text
            variant="body"
            numberOfLines={1}
            style={{
              flex: 1,
              color:
                value === undefined ? surface.placeholder : surface.content,
            }}
          >
            {value ?? label}
          </Text>
          <FigmaChevronDownIcon size={16} color={surface.placeholder} />
        </Box>
      </Pressable>
      {isOpen
        ? options.map((option) => (
            <Pressable
              key={option.id}
              disabled={!editable}
              onPress={() => {
                onSelect(option.id)
                setIsOpen(false)
              }}
              accessibilityRole="radio"
              accessibilityState={{ selected: option.id === value }}
              accessibilityLabel={option.labelFallback}
            >
              <Box
                style={[styles.option, { backgroundColor: theme.form.field }]}
              >
                <Text
                  variant="body"
                  style={{ color: theme.content.elements.primary }}
                >
                  {option.labelFallback}
                </Text>
              </Box>
            </Pressable>
          ))
        : null}
    </>
  )
}

/**
 * A box's fill and the colours that read on it (259:12593).
 *
 * A refused answer takes the yellow whether it is loose or a row of a card:
 * the card clips, so a filled row still follows the card's corners.
 */
function fieldSurface(
  theme: ReturnType<typeof useTheme>,
  grouped: boolean,
  invalid: boolean,
): { fill: string; content: string; placeholder: string } {
  if (invalid) {
    return theme.form.fieldInvalid
  }
  return {
    fill: grouped ? 'transparent' : theme.form.field,
    content: theme.content.elements.primary,
    placeholder: theme.content.elements.tertiary,
  }
}

// Only a SELECT carries options, and it always carries them.
const selectedLabel = (
  field: Extract<FormField, { type: 'SELECT' }>,
  value: string,
): string | undefined =>
  field.options.find((option) => option.id === value)?.labelFallback

const styles = StyleSheet.create({
  box: {
    height: KYC_FORM_METRICS.fieldHeight,
    borderRadius: KYC_FORM_METRICS.fieldRadius,
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    justifyContent: 'center',
  },
  // A card draws the corners for every row it holds.
  boxGrouped: { borderRadius: 0 },
  boxOptional: {
    paddingHorizontal: KYC_FORM_METRICS.optionalPaddingHorizontal,
  },
  chooser: { justifyContent: 'space-between' },
  // Undo the platform's own padding so the field's height is the box's.
  input: { padding: 0 },
  inputCentered: { textAlign: 'center' },
  optional: {
    position: 'absolute',
    left: KYC_FORM_METRICS.optionalPaddingHorizontal,
    bottom: KYC_FORM_METRICS.optionalCaptionBottom,
    fontSize: 11,
    lineHeight: 13,
    letterSpacing: -0.22,
  },
  option: {
    borderRadius: KYC_FORM_METRICS.fieldRadius,
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    paddingVertical: 12,
  },
})
